/**
 * Roles that may log a recall to work. One list for the dashboard route
 * policy, the `/roster/recalls/new` page guard and the `logRecall` action, so
 * the three cannot drift apart again (WP-B74 / BH-RTE-13).
 *
 * It is exactly the list the database accepts in
 * `log_shift_recall_event_controlled`. A role the database refuses must not be
 * shown the page: it would fill in the form and be refused on save. Admitting
 * the payroll-review roles (Payroll Officer, Region Manager, Region Finance
 * Officer) or Team Leader needs a change to that function first.
 */
export const RECALL_LOG_ROLES: readonly string[] = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'FACILITY_MANAGER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'FINANCE_OFFICER',
];

/**
 * Roles that log a recall only for themselves. Team Leader joined the list
 * through the payroll-review roles; it has no facility or region scope of its
 * own, so it is held to its own recalls rather than the whole provider.
 */
export const RECALL_LOG_SELF_ONLY_ROLES: ReadonlySet<string> = new Set([
  'CARE_WORKER',
  'ENROLLED_NURSE',
  'REGISTERED_NURSE',
  'TEAM_LEADER',
]);

/** Roles that could always log for any worker in their provider. */
const RECALL_LOG_PROVIDER_WIDE_ROLES: ReadonlySet<string> = new Set([
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
]);

/**
 * Every other role (facility, region and the payroll-review roles added when
 * the lists were aligned) must pass the database staff-scope check before it
 * acts for another worker. Unknown roles are scoped, never waved through.
 */
export function recallLogRoleNeedsStaffScope(role: string): boolean {
  return !RECALL_LOG_PROVIDER_WIDE_ROLES.has(role) && !RECALL_LOG_SELF_ONLY_ROLES.has(role);
}
